/**
 * Users → Activity — Tenant-wide identity activity feed (W1a-11).
 *
 * Promotes the per-user Activity tab from W1a-02 to a tenant-wide stream.
 * Every activity-class audit row across every user in the tenant, with
 * filters: user / type / outcome / date range. Click a row to drill into
 * `/users/$userId?tab=activity` (the per-user view).
 *
 * Backed by `GET /api/v1/users/activity` (permission `read:audit`,
 * admin+ in v1). Members + viewers see the empty-rendered RBAC fallback.
 *
 * Filter state syncs to the URL query string (Zod-validated) so refresh
 * preserves the view + deep-links work. Pagination uses real server
 * pagination — admin operators may have very large feeds.
 */

import {
  createFileRoute,
  useNavigate,
  useSearch,
  Link,
} from '@tanstack/react-router';
import { useState, useMemo, useEffect } from 'react';
import { z } from 'zod';
import {
  Search,
  Activity as ActivityIcon,
  CheckCircle2,
  XCircle,
  Shield,
  KeyRound,
  Eye,
  User as UserIcon,
  X as XIcon,
  Lock,
  LogOut,
  LogIn,
  RefreshCw,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ShieldAlert,
  ExternalLink,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  useTenantActivity,
  useTenantActivityStats,
  useUsers,
  type UserActivityEventType,
  type TenantActivityRow,
} from '@/lib/use-users';
import { useHasPermission } from '@/lib/use-has-permission';
// B2e-T1 — THE identity-activity vocabulary: the filter alphabet this page
// offers, the card -> filter map, and the Zod enum the URL state parses with.
import {
  USER_ACTIVITY_FILTER_TYPES,
  USER_ACTIVITY_STAT_TYPES,
  userActivityFilterTypeSchema,
} from '@argusai/shared/schemas/user-activity';
import { summariseUserAgent } from '@/lib/format';

// ─── URL state schema ─────────────────────────────────────────────────────

/**
 * The W1a-11 filter state syncs to the URL query string so refresh
 * preserves the view and deep-links carry the filters. All keys
 * optional. Unknown keys are stripped at parse time.
 */
const activitySearchSchema = z.object({
  uid: z.string().uuid().optional(), // userId
  // B2e-T1 — the vocabulary's own alphabet. The hand-written copy this
  // replaces offered `session_terminated`, a verb no emitter writes, so a
  // deep-link carrying it rendered an empty feed forever.
  t: userActivityFilterTypeSchema.optional(),
  o: z.enum(['success', 'failure']).optional(),
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  pg: z.coerce.number().int().min(1).optional(),
});
type ActivitySearch = z.infer<typeof activitySearchSchema>;

export const Route = createFileRoute('/users/activity')({
  component: ActivityPage,
  validateSearch: (raw): ActivitySearch => activitySearchSchema.parse(raw),
});

// ─── Display tables ───────────────────────────────────────────────────────

type FilterableType = NonNullable<ActivitySearch['t']>;

/**
 * The filter dropdown, BUILT FROM the vocabulary (B2e-T1).
 *
 * The list used to be written out here, and it drifted: it offered "MFA
 * challenge", "MFA enabled", "MFA disabled" and "Session terminated" — four
 * choices that could only ever return an empty page, because no emitter has
 * ever written the actions behind them. Deriving the options means a verb can
 * only be offered once an action exists for it, and the label table below is
 * exhaustive over the alphabet by type, so adding a verb without a label does
 * not compile.
 */
const TYPE_LABELS: Record<FilterableType, string> = {
  login: 'Login (success)',
  login_failed: 'Login (failure)',
  login_context_selected: 'Tenant selected',
  tenant_switched: 'Tenant switched',
  logout: 'Logout',
  token_refresh: 'Token refresh',
  token_rotation_violation: 'Token rotation violation',
  password_changed: 'Password changed',
  password_reset: 'Password reset',
  mfa_reset: 'MFA reset',
  impersonation_started: 'Impersonation started',
  impersonation_ended: 'Impersonation ended',
  session_revoked: 'Session revoked',
};

const TYPE_OPTIONS: { value: FilterableType | 'all'; label: string }[] = [
  { value: 'all', label: 'All types' },
  ...USER_ACTIVITY_FILTER_TYPES.map((value) => ({
    value,
    label: TYPE_LABELS[value],
  })),
];

interface TypeStyle {
  icon: typeof LogIn;
  className: string;
  label: string;
}

// Closes #163: row models for the rendered list. `single` is the
// existing per-event row. `group` collapses N adjacent token_refresh
// events for the same user inside one calendar hour. Login / failure /
// MFA / impersonation are NEVER grouped.
type ActivityRow =
  | { kind: 'single'; event: TenantActivityRow }
  | {
      kind: 'group';
      userId: string;
      bucketStart: string; // ISO timestamp of the newest event in the group
      events: TenantActivityRow[];
    };

function sameHour(aIso: string, bIso: string): boolean {
  const a = new Date(aIso);
  const b = new Date(bIso);
  return (
    a.getUTCFullYear() === b.getUTCFullYear() &&
    a.getUTCMonth() === b.getUTCMonth() &&
    a.getUTCDate() === b.getUTCDate() &&
    a.getUTCHours() === b.getUTCHours()
  );
}

function groupKey(userId: string, isoHourAnchor: string): string {
  const d = new Date(isoHourAnchor);
  const bucket = new Date(
    Date.UTC(
      d.getUTCFullYear(),
      d.getUTCMonth(),
      d.getUTCDate(),
      d.getUTCHours()
    )
  ).toISOString();
  return `${userId}|${bucket}`;
}

const TYPE_STYLE: Record<UserActivityEventType, TypeStyle> = {
  login: {
    icon: LogIn,
    className: 'text-emerald-600 dark:text-emerald-400',
    label: 'Login',
  },
  login_failed: {
    icon: XCircle,
    className: 'text-red-600 dark:text-red-400',
    label: 'Login failed',
  },
  logout: {
    icon: LogOut,
    className: 'text-muted-foreground',
    label: 'Logout',
  },
  token_refresh: {
    icon: RefreshCw,
    className: 'text-muted-foreground',
    label: 'Token refresh',
  },
  password_changed: {
    icon: KeyRound,
    className: 'text-blue-600 dark:text-blue-400',
    label: 'Password changed',
  },
  password_reset: {
    icon: KeyRound,
    className: 'text-amber-600 dark:text-amber-400',
    label: 'Password reset',
  },
  login_context_selected: {
    icon: LogIn,
    className: 'text-emerald-600 dark:text-emerald-400',
    label: 'Tenant selected',
  },
  tenant_switched: {
    icon: LogIn,
    className: 'text-emerald-600 dark:text-emerald-400',
    label: 'Tenant switched',
  },
  mfa_reset: {
    icon: Shield,
    className: 'text-amber-600 dark:text-amber-400',
    label: 'MFA reset',
  },
  impersonation_started: {
    icon: Eye,
    className: 'text-amber-600 dark:text-amber-400',
    label: 'Impersonation started',
  },
  impersonation_ended: {
    icon: Eye,
    className: 'text-muted-foreground',
    label: 'Impersonation ended',
  },
  session_revoked: {
    icon: XIcon,
    className: 'text-red-600 dark:text-red-400',
    label: 'Session revoked',
  },
  token_rotation_violation: {
    icon: ShieldAlert,
    className: 'text-red-600 dark:text-red-400',
    label: 'Token rotation violation',
  },
  other: {
    icon: ActivityIcon,
    className: 'text-muted-foreground',
    label: 'Activity',
  },
};

// ─── Component ────────────────────────────────────────────────────────────

const PAGE_SIZE = 50;

function ActivityPage() {
  const navigate = useNavigate();
  const sp = useSearch({ from: '/users/activity' });

  const canRead = useHasPermission('read', 'audit');

  // Filter state — initialized from URL params so deep-links + refresh
  // round-trip cleanly.
  const [userIdFilter, setUserIdFilter] = useState<string>(sp.uid ?? 'all');
  const [typeFilter, setTypeFilter] = useState<FilterableType | 'all'>(
    sp.t ?? 'all'
  );
  const [outcomeFilter, setOutcomeFilter] = useState<
    'all' | 'success' | 'failure'
  >(sp.o ?? 'all');
  const [search, setSearch] = useState('');
  // B2e-T1 review (BUG-01) — THE WINDOW IS PART OF THE FILTER.
  //
  // The cards are time-windowed ("Logins (24h)") and the feed was not: the
  // click-through set only the type, so clicking a card that said 3 opened a
  // list of 5,000. `from`/`to` had been in the URL schema since W1a-11 and
  // nothing ever wrote them. This holds the lower bound the card counted from,
  // so the number and the list it drills into are answers to one question.
  const [fromFilter, setFromFilter] = useState<string | undefined>(sp.from);
  const [page, setPage] = useState<number>(sp.pg ?? 1);

  // Local-only client-side text search (api filter set is structural only;
  // free-text search needs a server-side index that's a Wave-1d follow-up
  // — F-W1a-11.X). Here we filter the visible page for the operator.

  // Sync filter changes back to the URL (replace mode — no history spam).
  useEffect(() => {
    const next: ActivitySearch = {};
    if (userIdFilter !== 'all') next.uid = userIdFilter;
    if (typeFilter !== 'all') next.t = typeFilter;
    if (outcomeFilter !== 'all') next.o = outcomeFilter;
    if (fromFilter) next.from = fromFilter;
    if (page > 1) next.pg = page;
    navigate({ to: '/users/activity', search: next, replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIdFilter, typeFilter, outcomeFilter, fromFilter, page]);

  // Whenever a *filter* changes (not page), reset to page 1 so the user
  // doesn't end up viewing page 5 of a freshly-narrowed result set.
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIdFilter, typeFilter, outcomeFilter, fromFilter]);

  // Live data — only fetched when caller has permission. The query is
  // guarded by `canRead` so a member/viewer doesn't burn API attempts on
  // a 403.
  const { events, pagination, isLoading, error } = useTenantActivity(
    canRead
      ? {
          page,
          pageSize: PAGE_SIZE,
          userId: userIdFilter !== 'all' ? userIdFilter : undefined,
          type: typeFilter !== 'all' ? typeFilter : undefined,
          outcome: outcomeFilter !== 'all' ? outcomeFilter : undefined,
          // B2e-T1 review (BUG-01) — the card's window, carried through to
          // the list. `GET /users/activity` has taken `fromDate` since
          // W1a-11; nothing had ever sent it.
          fromDate: fromFilter,
        }
      : {}
  );

  // User picker options — fetch the user list once for the dropdown.
  // We don't fetch the full set on a per-page basis; just the first 200
  // for the picker (matches the Add-Members-dialog pattern in W1a-10).
  const { users: userOptions } = useUsers({ pageSize: 200 });

  const filteredEvents = useMemo(() => {
    if (!search.trim()) return events;
    const q = search.toLowerCase();
    return events.filter((e) => {
      const blob = `${e.userEmail} ${e.userName ?? ''} ${e.ip ?? ''} ${
        e.details ?? ''
      } ${e.userAgent ?? ''}`.toLowerCase();
      return blob.includes(q);
    });
  }, [events, search]);

  // Closes #163: collapse runs of TOKEN_REFRESH for the same user inside
  // the same calendar hour into a single grouped row. Login / failure /
  // MFA / impersonation events are NEVER grouped — they remain individual
  // rows because they're high-signal.
  //
  // The grouping is purely a view-layer concern; the underlying
  // `filteredEvents` array is unchanged so pagination + counts stay
  // honest. We only collapse adjacent rows (post-filter, post-sort) so
  // operators can still see chronological order, and the search text
  // already filters group members consistently.
  const groupedRows = useMemo<ActivityRow[]>(() => {
    const out: ActivityRow[] = [];
    for (const e of filteredEvents) {
      const last = out[out.length - 1];
      if (
        e.type === 'token_refresh' &&
        last &&
        last.kind === 'group' &&
        last.userId === e.userId &&
        sameHour(last.bucketStart, e.timestamp)
      ) {
        last.events.push(e);
        // Newest event is at index 0 from the API, so the group's
        // `bucketStart` (the earliest event in the group) drifts later
        // as we descend; keep the group anchored at its first encountered
        // event (which is the newest event in the bucket because we
        // iterate newest-first).
        continue;
      }
      if (e.type === 'token_refresh') {
        out.push({
          kind: 'group',
          userId: e.userId,
          bucketStart: e.timestamp,
          events: [e],
        });
        continue;
      }
      out.push({ kind: 'single', event: e });
    }
    return out;
  }, [filteredEvents]);

  // Track which token-refresh groups are expanded. Default = collapsed.
  // A group's identity key is `${userId}|${ISO hour bucket}`.
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    () => new Set()
  );
  function toggleGroup(key: string) {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  // B2e-T1 — THE NUMBERS ARE THE SERVER'S.
  //
  // This page used to compute the four cards here, in a `for` loop over
  // `events` — the 50 rows of the feed it happened to have loaded — and label
  // them "(24h)". On a busy tenant "Logins (24h)" meant the last hour; on a
  // quiet one, the last month. The count the server can take exactly is the
  // count the operator is shown, over the same audit rows with the same
  // predicate, and the card's click-through filters to exactly what it
  // counted.
  //
  // B2e-T1 review (R1) — AND A NUMBER NOBODY COULD TAKE IS NOT ZERO.
  //
  // `error` and `isLoading` used to be dropped here and every card read
  // `stats?.x ?? 0`, so a 403, a timeout or an API restart rendered
  // "Impersonations (7d): 0" — above a feed, fetched by a DIFFERENT query,
  // happily listing impersonation rows. Zero is an answer; "we could not ask"
  // is not, and principle 4 says nothing fails open, the display least of all.
  const {
    stats,
    isLoading: statsLoading,
    error: statsError,
  } = useTenantActivityStats({ window: '24h', enabled: canRead });

  /** What the cards are: a number, a wait, or a failure. Never a silent 0. */
  const statsState: 'ready' | 'loading' | 'error' = statsError
    ? 'error'
    : stats
      ? 'ready'
      : statsLoading
        ? 'loading'
        : 'error';
  const statsErrorMessage =
    (statsError as Error | undefined)?.message ??
    'The activity statistics could not be loaded.';

  /**
   * B2e-T1 review (BUG-01) — SHOW EXACTLY WHAT THE CARD COUNTED.
   *
   * A card counts one type, tenant-wide, since `windowStart`. The
   * click-through used to apply only the type: no window, and the active user
   * filter left in place, so "Logins (24h) 3" opened a list of every login
   * ever, or of Alice's logins while the card counted everyone's. All three
   * parts of the question move together now, or the two disagree again.
   */
  const showWhatTheCardCounted = (
    type: FilterableType,
    outcome: 'all' | 'success' | 'failure',
    windowStart: string
  ) => {
    setTypeFilter(type);
    setOutcomeFilter(outcome);
    setFromFilter(windowStart);
    setUserIdFilter('all');
  };

  const hasFilters =
    typeFilter !== 'all' ||
    outcomeFilter !== 'all' ||
    userIdFilter !== 'all' ||
    !!fromFilter ||
    !!search;

  const totalPages = pagination?.totalPages ?? 1;
  const totalCount = pagination?.totalCount ?? 0;

  // RBAC fallback — render an empty-state card directing the operator
  // to ask for the appropriate permission. The route IS still registered
  // for non-admin users; the API will 403 if the URL is hit directly.
  if (!canRead) {
    return (
      <div className="flex flex-col h-full p-6">
        <Card>
          <CardContent className="p-12 text-center">
            <ShieldAlert className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
            <p className="text-sm font-medium">
              You don&apos;t have permission to view tenant activity
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              The Activity feed requires the <code>read:audit</code>{' '}
              permission. Contact your tenant administrator.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full p-6 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">User Activity</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Identity events across the tenant — logins, MFA, password changes,
            session revocations, impersonations.
          </p>
        </div>
      </div>

      {/* Stats cards. B2e-T1: every number here is GET /users/activity/stats,
          counted by the server over the same audit rows the feed below lists.
          Clicking a card applies the filter the card counted, so the number
          and the list it drills into can never disagree. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <StatCard
          label="Logins (24h)"
          value={stats?.logins ?? null}
          state={statsState}
          errorMessage={statsErrorMessage}
          icon={LogIn}
          active={
            typeFilter === USER_ACTIVITY_STAT_TYPES.logins &&
            outcomeFilter === 'success'
          }
          onClick={
            stats
              ? () =>
                  showWhatTheCardCounted(
                    USER_ACTIVITY_STAT_TYPES.logins,
                    'success',
                    stats.windowStart
                  )
              : undefined
          }
          tooltip="Successful logins across the tenant in the last 24 hours — click to filter the feed to them"
        />
        <StatCard
          label="Failed logins (24h)"
          value={stats?.failedLogins ?? null}
          state={statsState}
          errorMessage={statsErrorMessage}
          icon={XCircle}
          variant={(stats?.failedLogins ?? 0) > 0 ? 'warn' : 'default'}
          active={typeFilter === USER_ACTIVITY_STAT_TYPES.failedLogins}
          onClick={
            stats
              ? () =>
                  showWhatTheCardCounted(
                    USER_ACTIVITY_STAT_TYPES.failedLogins,
                    'all',
                    stats.windowStart
                  )
              : undefined
          }
          tooltip="Refused sign-in attempts across the tenant in the last 24 hours — click to filter the feed to them"
        />
        {/* B2e-T1 — this card used to read "MFA challenges (24h)" and could
            only ever show 0: there is no MFA challenge in this product, so no
            emitter has ever written one. It shows the MFA event that DOES
            happen — an administrator clearing somebody's enrolment. */}
        <StatCard
          label="MFA resets (24h)"
          value={stats?.mfaResets ?? null}
          state={statsState}
          errorMessage={statsErrorMessage}
          icon={Shield}
          active={typeFilter === USER_ACTIVITY_STAT_TYPES.mfaResets}
          onClick={
            stats
              ? () =>
                  showWhatTheCardCounted(
                    USER_ACTIVITY_STAT_TYPES.mfaResets,
                    'all',
                    stats.windowStart
                  )
              : undefined
          }
          tooltip="MFA enrolments cleared by an administrator in the last 24 hours — click to filter the feed to them"
        />
        <StatCard
          label="Impersonations (7d)"
          value={stats?.impersonations ?? null}
          state={statsState}
          errorMessage={statsErrorMessage}
          icon={Eye}
          variant={(stats?.impersonations ?? 0) > 0 ? 'warn' : 'default'}
          active={typeFilter === USER_ACTIVITY_STAT_TYPES.impersonations}
          onClick={
            // This card counts its OWN, wider window (the server names it in
            // the answer rather than the page assuming 7 days), so the
            // click-through carries that one, not the other three's.
            stats
              ? () =>
                  showWhatTheCardCounted(
                    USER_ACTIVITY_STAT_TYPES.impersonations,
                    'all',
                    stats.impersonationWindowStart
                  )
              : undefined
          }
          tooltip="Sessions started inside somebody else's account in the last 7 days — click to filter the feed to them"
        />
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-3 flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search current page (email, IP, user agent, details)…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 pl-8 text-xs"
            />
          </div>

          <Select value={userIdFilter} onValueChange={setUserIdFilter}>
            <SelectTrigger className="h-8 w-44 text-xs">
              <SelectValue placeholder="All users" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">
                All users
              </SelectItem>
              {userOptions.map((u) => (
                <SelectItem key={u.id} value={u.id} className="text-xs">
                  {u.fullName || u.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={typeFilter}
            onValueChange={(v) =>
              setTypeFilter(v as FilterableType | 'all')
            }
          >
            <SelectTrigger className="h-8 w-44 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TYPE_OPTIONS.map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                  className="text-xs"
                >
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={outcomeFilter}
            onValueChange={(v) =>
              setOutcomeFilter(v as typeof outcomeFilter)
            }
          >
            <SelectTrigger className="h-8 w-32 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">
                All outcomes
              </SelectItem>
              <SelectItem value="success" className="text-xs">
                Success
              </SelectItem>
              <SelectItem value="failure" className="text-xs">
                Failure
              </SelectItem>
            </SelectContent>
          </Select>

          {/* B2e-T1 review (BUG-01) — the window a card handed over is a
              filter like any other, so it is VISIBLE and clearable. An
              invisible bound is how "Showing 3 of 5,000" happens. */}
          {fromFilter && (
            <Badge
              variant="outline"
              className="h-8 px-2 text-xs font-normal gap-1"
            >
              Since {new Date(fromFilter).toLocaleString()}
              <button
                type="button"
                aria-label="Clear the time window"
                title="Clear the time window"
                onClick={() => setFromFilter(undefined)}
                className="ml-0.5 text-muted-foreground hover:text-foreground"
              >
                <XIcon className="h-3 w-3" />
              </button>
            </Badge>
          )}

          {hasFilters && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs"
              onClick={() => {
                setSearch('');
                setUserIdFilter('all');
                setTypeFilter('all');
                setOutcomeFilter('all');
                setFromFilter(undefined);
              }}
            >
              <XIcon className="h-3 w-3 mr-1" /> Reset
            </Button>
          )}

          <div className="flex-1" />
          <span className="text-xs text-muted-foreground tabular-nums">
            {totalCount.toLocaleString()} events
          </span>
        </CardContent>
      </Card>

      {/* Events list */}
      <div className="rounded-lg border bg-card shadow-sm flex-1 overflow-auto">
        {isLoading ? (
          <div className="py-16 text-center text-xs text-muted-foreground">
            Loading…
          </div>
        ) : error ? (
          <div className="py-16 text-center">
            <ShieldAlert className="h-10 w-10 mx-auto text-red-500/50 mb-3" />
            <p className="text-sm font-medium">Failed to load activity</p>
            <p className="text-xs text-muted-foreground mt-2">
              {(error as Error)?.message ?? 'Unknown error'}
            </p>
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="py-16 text-center">
            <ActivityIcon className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-sm font-medium">
              {hasFilters
                ? 'No events match your filters'
                : 'No activity yet'}
            </p>
            {hasFilters && (
              <p className="text-xs text-muted-foreground mt-2">
                Try widening the date range or clearing filters.
              </p>
            )}
          </div>
        ) : (
          <div className="divide-y">
            {groupedRows.map((row, idx) => {
              if (row.kind === 'single') {
                return <EventRow key={row.event.id} event={row.event} />;
              }
              // Single-event "group" still renders as a normal row — no
              // value in collapsing one event into a chevron.
              if (row.events.length === 1) {
                return (
                  <EventRow key={row.events[0].id} event={row.events[0]} />
                );
              }
              const key = groupKey(row.userId, row.bucketStart);
              return (
                <TokenRefreshGroupRow
                  key={`g-${row.userId}-${row.bucketStart}-${idx}`}
                  events={row.events}
                  expanded={expandedGroups.has(key)}
                  onToggle={() => toggleGroup(key)}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground tabular-nums">
            Page {page} of {totalPages} · {totalCount.toLocaleString()} total
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <Input
              type="number"
              min={1}
              max={totalPages}
              value={page}
              onChange={(e) => {
                const n = Number(e.target.value);
                if (Number.isFinite(n) && n >= 1 && n <= totalPages) {
                  setPage(n);
                }
              }}
              className="h-7 w-16 text-xs text-center"
            />
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Row ──────────────────────────────────────────────────────────────────

function EventRow({ event }: { event: TenantActivityRow }) {
  const style = TYPE_STYLE[event.type] ?? TYPE_STYLE.other;
  const Icon = style.icon;

  return (
    <Link
      to={'/users/$userId' as any}
      params={{ userId: event.userId } as any}
      search={{ tab: 'activity' } as any}
      className="block hover:bg-muted/30 transition-colors"
    >
      <div className="flex items-start gap-3 px-4 py-3">
        <div
          className={cn(
            'h-7 w-7 rounded-md bg-muted/40 flex items-center justify-center shrink-0',
            style.className
          )}
        >
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium">{style.label}</span>
            {event.outcome === 'failure' && (
              <Badge
                variant="outline"
                className="text-[9px] px-1.5 py-0 h-4 bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30"
              >
                failure
              </Badge>
            )}
          </div>
          {event.details && (
            <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
              {event.details}
            </div>
          )}
          <div className="flex items-center gap-3 text-[10px] text-muted-foreground mt-1 flex-wrap tabular-nums">
            <span className="inline-flex items-center gap-1">
              <UserIcon className="h-3 w-3" />
              <span className="font-medium text-foreground">
                {event.userName || event.userEmail}
              </span>
              {event.userName && (
                <span className="opacity-70">({event.userEmail})</span>
              )}
            </span>
            {event.ip && (
              <>
                <span>·</span>
                <code className="text-[10px] font-mono">{event.ip}</code>
              </>
            )}
            {event.userAgent && (() => {
              // Closes #257: title and aria-label show the SAME
              // humanised summary as the visible text. Round 3
              // (#221) already humanised the visible label, but the
              // raw user-agent string still leaked through `title`
              // (visible on hover) and `aria-label` (read by screen
              // readers / a11y tools). Both are now the summary.
              const ua = summariseUserAgent(event.userAgent);
              return (
                <>
                  <span>·</span>
                  <span className="truncate max-w-[180px]" title={ua} aria-label={ua}>
                    {ua}
                  </span>
                </>
              );
            })()}
          </div>
        </div>
        <div className="text-[10px] text-muted-foreground tabular-nums shrink-0 text-right flex items-center gap-1">
          <div>
            <div>{timeAgo(event.timestamp)}</div>
            <div>
              {new Date(event.timestamp).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </div>
          </div>
          <ExternalLink className="h-3 w-3 opacity-40" />
        </div>
      </div>
    </Link>
  );
}

// Closes #163: collapsed row for N adjacent token_refresh events for
// the same user inside the same calendar hour. Click to expand into
// the individual per-event rows. Login / failure / MFA / impersonation
// events are NEVER routed here — they always render as `<EventRow />`.
function TokenRefreshGroupRow({
  events,
  expanded,
  onToggle,
}: {
  events: TenantActivityRow[];
  expanded: boolean;
  onToggle: () => void;
}) {
  // Newest -> oldest. The group's "anchor" is the newest event.
  const newest = events[0];
  const oldest = events[events.length - 1];
  const count = events.length;
  const userName = newest.userName || newest.userEmail;

  return (
    <div className="block">
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left hover:bg-muted/30 transition-colors"
        aria-expanded={expanded}
        aria-label={`Token refreshed ${count}x for ${newest.userEmail}; click to ${expanded ? 'collapse' : 'expand'}`}
        title={`Token refreshed ${count}x for ${newest.userEmail}`}
      >
        <div className="flex items-start gap-3 px-4 py-3">
          <div className="h-7 w-7 rounded-md bg-muted/40 flex items-center justify-center shrink-0 text-muted-foreground">
            <RefreshCw className="h-3.5 w-3.5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium">
                Token refreshed{' '}
                <span className="tabular-nums">{count}x</span>
              </span>
              <Badge
                variant="outline"
                className="text-[9px] px-1.5 py-0 h-4"
              >
                grouped
              </Badge>
            </div>
            <div className="flex items-center gap-3 text-[10px] text-muted-foreground mt-1 flex-wrap tabular-nums">
              <span className="inline-flex items-center gap-1">
                <UserIcon className="h-3 w-3" />
                <span className="font-medium text-foreground">
                  {userName}
                </span>
                {newest.userName && (
                  <span className="opacity-70">({newest.userEmail})</span>
                )}
              </span>
              <span>·</span>
              <span>
                {new Date(oldest.timestamp).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                {' – '}
                {new Date(newest.timestamp).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>
          </div>
          <div className="text-[10px] text-muted-foreground tabular-nums shrink-0 text-right flex items-center gap-1">
            <div>
              <div>{timeAgo(newest.timestamp)}</div>
              <div>
                {new Date(newest.timestamp).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </div>
            </div>
            <ChevronDown
              className={cn(
                'h-3 w-3 opacity-60 transition-transform',
                expanded && 'rotate-180'
              )}
            />
          </div>
        </div>
      </button>
      {expanded && (
        <div className="border-l-2 border-muted-foreground/20 ml-8 divide-y bg-muted/10">
          {events.map((e) => (
            <EventRow key={e.id} event={e} />
          ))}
        </div>
      )}
    </div>
  );
}

// `summariseUserAgent` lives in `@/lib/format` so the user-detail
// Activity tab and the tenant-wide /users/activity feed share the
// same rendering (#221).

// Closes #164: compact stats card matching the visual style of the
// Users tab StatsBar. Click dispatches the matching filter so the
// operator can drill into the underlying rows. Counts are derived
// from the currently loaded page only — true tenant-wide aggregates
// would require a Wave-2 server-side stats endpoint.
/**
 * One number the server took, with the two states that are not a number.
 *
 * B2e-T1 review (R1). `value` was `number` and every caller passed
 * `stats?.x ?? 0`, which turned "the request failed" into the reading "zero
 * of these happened" — the one reading an operator must never be handed by
 * accident, because it is indistinguishable from a quiet, healthy tenant.
 * A card with no answer says so, and does not offer a click-through to a
 * window it does not know.
 */
function StatCard({
  label,
  value,
  icon: Icon,
  variant = 'default',
  active = false,
  onClick,
  tooltip,
  state = 'ready',
  errorMessage,
}: {
  label: string;
  /** The count, or null when there is no answer to render. */
  value: number | null;
  icon: typeof LogIn;
  variant?: 'default' | 'warn';
  active?: boolean;
  /** Absent while there is no answer — the card is then not a button. */
  onClick?: () => void;
  tooltip: string;
  state?: 'ready' | 'loading' | 'error';
  errorMessage?: string;
}) {
  const ready = state === 'ready' && value !== null;
  const warn = ready && variant === 'warn' && (value ?? 0) > 0;
  const display = ready
    ? (value as number).toLocaleString()
    : state === 'loading'
      ? '…'
      : '—';
  const title = ready
    ? tooltip
    : state === 'loading'
      ? `${label}: still loading`
      : `${label}: unavailable. ${errorMessage ?? ''}`.trim();
  const spoken = ready
    ? `${label}: ${value}. ${tooltip}`
    : state === 'loading'
      ? `${label}: loading`
      : `${label}: unavailable — ${errorMessage ?? 'the count could not be loaded'}`;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      title={title}
      aria-label={spoken}
      aria-pressed={active}
      className={cn(
        'text-left rounded border bg-card p-3 transition-colors',
        onClick
          ? 'hover:bg-muted/40 hover:border-foreground/20'
          : 'cursor-default',
        active && 'border-primary/50 bg-primary/5',
        state === 'error' && 'border-red-500/40 bg-red-500/5',
        warn && 'border-amber-500/40 bg-amber-500/5'
      )}
    >
      <div className="flex items-center gap-2">
        {state === 'error' ? (
          <ShieldAlert className="h-3.5 w-3.5 shrink-0 text-red-600 dark:text-red-400" />
        ) : (
          <Icon
            className={cn(
              'h-3.5 w-3.5 shrink-0',
              warn
                ? 'text-amber-600 dark:text-amber-400'
                : 'text-muted-foreground'
            )}
          />
        )}
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground truncate">
          {label}
        </span>
      </div>
      <div
        className={cn(
          'text-xl font-semibold tabular-nums mt-1',
          !ready && 'text-muted-foreground'
        )}
      >
        {display}
      </div>
      {state === 'error' && (
        <div className="text-[10px] text-red-600 dark:text-red-400 mt-0.5 truncate">
          Unavailable
        </div>
      )}
    </button>
  );
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60_000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (minutes < 60) return minutes < 1 ? 'Just now' : `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

// LOCK + ICONS reserved for future expansion (e.g. account_locked event
// handling once W1a-13's USER_STATUS_CHANGED makes it onto the activity
// whitelist). Imported above to keep the lint green if added later.
void Lock;
void CheckCircle2;
